import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { X, ChevronDown, Loader2 } from 'lucide-react'

export function LoginPage() {
  const { signInWithPassword, signInWithGitHub, signInWithAzure } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showLegacyLogin, setShowLegacyLogin] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !password) {
      toast.error('Fyll ut e-post og passord')
      return
    }

    setIsLoading(true)
    const { error } = await signInWithPassword(email, password)
    setIsLoading(false)

    if (error) {
      toast.error(error.message || 'Innlogging feilet')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-80 rounded-xl bg-card p-8 text-foreground">
        <h1 className="text-2xl font-bold text-center mb-8">Tomatic Admin</h1>

        {/* Microsoft Login - Primary */}
        <button
          onClick={signInWithAzure}
          className="w-full flex items-center justify-center gap-3 bg-[#0078D4] hover:bg-[#006cbd] text-white py-4 px-6 rounded-lg font-semibold transition-colors text-lg"
        >
          <svg viewBox="0 0 21 21" className="w-5 h-5 fill-current">
            <rect x="1" y="1" width="9" height="9" />
            <rect x="11" y="1" width="9" height="9" />
            <rect x="1" y="11" width="9" height="9" />
            <rect x="11" y="11" width="9" height="9" />
          </svg>
          Logg inn med Microsoft
        </button>

        {/* Divider */}
        <div className="flex items-center my-6">
          <div className="h-px flex-1 bg-border" />
          <span className="px-3 text-sm text-muted-foreground">Eller</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Legacy Login Toggle */}
        {!showLegacyLogin ? (
          <button
            onClick={() => setShowLegacyLogin(true)}
            className="w-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground py-2 transition-colors text-sm"
          >
            <ChevronDown className="w-4 h-4" />
            Logg inn med E-post / GitHub
          </button>
        ) : (
          <div className="relative border border-border rounded-lg p-4">
            {/* Close button */}
            <button
              onClick={() => setShowLegacyLogin(false)}
              className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Lukk"
            >
              <X className="w-4 h-4" />
            </button>

            <form onSubmit={handleSignIn} className="mt-2">
              <div className="text-sm">
                <label className="block text-muted-foreground mb-1">
                  E-post
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-border outline-none bg-card px-3 py-2 text-foreground focus:border-violet-400"
                />
              </div>

              <div className="mt-3 text-sm">
                <label className="block text-muted-foreground mb-1">
                  Passord
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-border outline-none bg-card px-3 py-2 text-foreground focus:border-violet-400"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-4 bg-violet-400 py-2 text-center text-gray-900 rounded-md font-semibold hover:bg-violet-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Logger inn...
                  </>
                ) : (
                  'Logg inn'
                )}
              </button>
            </form>

            {/* GitHub */}
            <div className="flex items-center my-4">
              <div className="h-px flex-1 bg-border" />
              <span className="px-3 text-xs text-muted-foreground">eller</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <button
              onClick={signInWithGitHub}
              className="w-full flex items-center justify-center gap-2 border border-border py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-4 h-4 fill-current">
                <path d="M16 0.396c-8.839 0-16 7.167-16 16 0 7.073 4.584 13.068 10.937 15.183 0.803 0.151 1.093-0.344 1.093-0.772 0-0.38-0.009-1.385-0.015-2.719-4.453 0.964-5.391-2.151-5.391-2.151-0.729-1.844-1.781-2.339-1.781-2.339-1.448-0.989 0.115-0.968 0.115-0.968 1.604 0.109 2.448 1.645 2.448 1.645 1.427 2.448 3.744 1.74 4.661 1.328 0.14-1.031 0.557-1.74 1.011-2.135-3.552-0.401-7.287-1.776-7.287-7.907 0-1.751 0.62-3.177 1.645-4.297-0.177-0.401-0.719-2.031 0.141-4.235 0 0 1.339-0.427 4.4 1.641 1.281-0.355 2.641-0.532 4-0.541 1.36 0.009 2.719 0.187 4 0.541 3.043-2.068 4.381-1.641 4.381-1.641 0.859 2.204 0.317 3.833 0.161 4.235 1.015 1.12 1.635 2.547 1.635 4.297 0 6.145-3.74 7.5-7.296 7.891 0.556 0.479 1.077 1.464 1.077 2.959 0 2.14-0.020 3.864-0.020 4.385 0 0.416 0.28 0.916 1.104 0.755 6.4-2.093 10.979-8.093 10.979-15.156 0-8.833-7.161-16-16-16z"></path>
              </svg>
              GitHub
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
